package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.documents.DocumentOpener
import com.evoapps.evoshift.domain.model.DocumentFiles
import com.evoapps.evoshift.domain.model.InvalidDocumentException
import com.evoapps.evoshift.domain.model.StaffCheck
import com.evoapps.evoshift.domain.model.StaffDocument
import com.evoapps.evoshift.domain.model.StaffDocumentType
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.usecase.documents.DeleteStaffDocumentUseCase
import com.evoapps.evoshift.domain.usecase.documents.GetStaffDocumentsUseCase
import com.evoapps.evoshift.domain.usecase.documents.OpenStaffDocumentUseCase
import com.evoapps.evoshift.domain.usecase.documents.PickedFile
import com.evoapps.evoshift.domain.usecase.documents.UploadStaffDocumentUseCase
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toDocumentUiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.documents_deleted
import evoshift.composeapp.generated.resources.documents_uploaded
import evoshift.composeapp.generated.resources.documents_uploaded_medical_recorded
import evoshift.composeapp.generated.resources.documents_uploaded_ohs_recorded
import evoshift.composeapp.generated.resources.documents_file_unreadable
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate

/** A picked file waiting for its type and expiry date (the upload dialog). */
data class DocumentDraft(
    val file: PickedFile,
    val type: StaffDocumentType = StaffDocumentType.CONTRACT,
    /** "YYYY-MM-DD" as typed; blank: no expiry. */
    val expiresOn: String = ""
) {
    val parsedExpiry: LocalDate? get() = expiresOn.trim().takeIf { it.isNotEmpty() }?.let { runCatching { LocalDate.parse(it) }.getOrNull() }

    /** A typed date that is not one. */
    val expiryInvalid: Boolean get() = expiresOn.isNotBlank() && parsedExpiry == null
}

data class StaffDocumentsUiState(
    /** Only until the first list arrives. */
    val isLoading: Boolean = true,
    /** Whose documents ("" while loading); [isMine]: the signed-in person's own. */
    val personName: String = "",
    val isMine: Boolean = false,
    /** The reader may see this list at all (the person, the workplace's HR readers). */
    val canView: Boolean = false,
    val documents: List<StaffDocument> = emptyList(),
    val canUpload: Boolean = false,
    /** HR readers: their uploads also record the medical exam / OHS validity (SH-47). */
    val recordsStaffDates: Boolean = false,
    val draft: DocumentDraft? = null,
    /** SH-161 F4: files dropped together, each getting the upload dialog in turn after [draft]. */
    val queue: List<PickedFile> = emptyList(),
    /** The upload under way: its file name and progress 0..1. */
    val uploading: Pair<String, Float>? = null,
    /** The document being downloaded to open. */
    val openingId: String? = null,
    val message: UiText? = null,
    val error: UiText? = null,
    /** Internal: who reads, for removing (HR readers any, the person their own uploads). */
    val readerId: String? = null,
    val readerIsHr: Boolean = false
) {
    fun canDelete(document: StaffDocument): Boolean = readerIsHr || (readerId != null && document.uploadedBy == readerId && isMine && canUpload)
}

/**
 * SH-122: one person's documents at one workplace (contract, medical certificate, OHS, other).
 * Opened by the person (profile, [userId] null) or the workplace's owner and financial managers
 * (employee form). Uploads show their progress; a removal disappears at once and comes back if
 * it fails. Nothing here is a signature.
 */
class StaffDocumentsViewModel(
    private val workplaceId: String,
    private val userId: String?,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val getDocuments: GetStaffDocumentsUseCase,
    private val uploadDocument: UploadStaffDocumentUseCase,
    private val deleteDocument: DeleteStaffDocumentUseCase,
    private val openDocument: OpenStaffDocumentUseCase,
    private val opener: DocumentOpener,
    private val users: UserRepository,
    private val observePlanActive: ObservePlanActiveUseCase? = null
) : ViewModel() {
    private val _uiState = MutableStateFlow(StaffDocumentsUiState())
    val uiState: StateFlow<StaffDocumentsUiState> = _uiState.asStateFlow()
    private val job = JobSlot()

    init {
        job.launch(viewModelScope) {
            combine(getCurrentUser(), observePlanActive.activeAt(workplaceId)) { user, planActive -> user to planActive }
                .distinctUntilChanged()
                .collect { (user, planActive) -> load(user, planActive) }
        }
    }

    private suspend fun load(user: User?, planActive: Boolean) {
        if (user == null) {
            _uiState.value = StaffDocumentsUiState(isLoading = false)
            return
        }
        val subject = userId ?: user.uid
        val mine = subject == user.uid
        val capabilities = user.capabilitiesAt(workplaceId, planActive)
        val hr = capabilities.shows(Gate.STAFF_DOCUMENTS)
        if (!mine && !hr) {
            _uiState.value = StaffDocumentsUiState(isLoading = false)
            return
        }
        val name = if (mine) user.displayName else users.getUserProfile(subject).getOrNull()?.displayName.orEmpty()
        val canUpload = capabilities.shows(Gate.STAFF_DOCUMENTS_EDITING) || (mine && capabilities.shows(Gate.UPLOAD_OWN_DOCUMENTS))
        _uiState.update {
            it.copy(
                personName = name, isMine = mine, canView = true, canUpload = canUpload,
                recordsStaffDates = capabilities.shows(Gate.STAFF_RECORDS_EDITING),
                readerId = user.uid, readerIsHr = hr && capabilities.shows(Gate.STAFF_DOCUMENTS_EDITING)
            )
        }
        refresh()
    }

    private suspend fun refresh() {
        val subject = userId ?: getCurrentUser().first()?.uid ?: return
        getDocuments(subject, workplaceId)
            .onSuccess { list -> _uiState.update { it.copy(isLoading = false, documents = list) } }
            .onFailure { e -> _uiState.update { it.copy(isLoading = false, error = e.toUiText()) } }
    }

    /** A file came back from the chooser (null: it could not be read). */
    fun onPicked(file: PickedFile?) {
        if (file == null) {
            _uiState.update { it.copy(error = UiText.Resource(Res.string.documents_file_unreadable)) }
            return
        }
        // Another one is being described or sent: this one waits its turn.
        if (_uiState.value.draft != null || _uiState.value.uploading != null) {
            _uiState.update { it.copy(queue = it.queue + file) }
            return
        }
        DocumentFiles.problemOf(file.bytes)?.let { problem ->
            _uiState.update { it.copy(error = InvalidDocumentException(problem).toUiText()) }
            return
        }
        _uiState.update { it.copy(draft = DocumentDraft(file)) }
    }

    fun setDraftType(type: StaffDocumentType) = _uiState.update { s -> s.copy(draft = s.draft?.copy(type = type)) }

    fun setDraftExpiry(text: String) = _uiState.update { s -> s.copy(draft = s.draft?.copy(expiresOn = text)) }

    fun cancelDraft() {
        _uiState.update { it.copy(draft = null) }
        showNext()
    }

    /**
     * SH-161 F4 (Play's Drag_Drop_Batch): several files dropped at once, one upload dialog after
     * another (each with its own type and expiry); a file that could not be read is said so.
     */
    fun onPickedMany(files: List<PickedFile?>) {
        files.forEach(::onPicked)
    }

    /** The next waiting file's dialog, once nothing is being described or sent. */
    private fun showNext() {
        while (true) {
            val state = _uiState.value
            if (state.draft != null || state.uploading != null) return
            val next = state.queue.firstOrNull() ?: return
            _uiState.update { it.copy(queue = it.queue.drop(1)) }
            onPicked(next)
        }
    }

    fun upload() {
        val state = _uiState.value
        val draft = state.draft ?: return
        if (draft.expiryInvalid || state.uploading != null) return
        val subject = userId ?: state.readerId ?: return
        val uploader = state.readerId ?: return
        _uiState.update { it.copy(draft = null, uploading = (draft.file.name ?: "") to 0f) }
        viewModelScope.launch {
            uploadDocument(
                userId = subject, workplaceId = workplaceId, type = draft.type, file = draft.file,
                expiresOn = draft.parsedExpiry, uploaderId = uploader, updateStaffDates = state.recordsStaffDates,
                onProgress = { progress -> _uiState.update { s -> s.uploading?.let { s.copy(uploading = it.first to progress) } ?: s } }
            ).onSuccess { done ->
                val message = when (done.updatedCheck) {
                    StaffCheck.MEDICAL_EXAM -> UiText.Resource(Res.string.documents_uploaded_medical_recorded)
                    StaffCheck.OHS_TRAINING -> UiText.Resource(Res.string.documents_uploaded_ohs_recorded)
                    null -> UiText.Resource(Res.string.documents_uploaded)
                }
                _uiState.update { s -> s.copy(uploading = null, documents = listOf(done.document) + s.documents, message = message) }
                showNext()
            }.onFailure { e ->
                _uiState.update { it.copy(uploading = null, error = e.toDocumentUiText()) }
                showNext()
            }
        }
    }

    fun delete(document: StaffDocument) {
        val before = _uiState.value.documents
        if (document !in before) return
        _uiState.update { it.copy(documents = before - document) }
        viewModelScope.launch {
            deleteDocument(document)
                .onSuccess { _uiState.update { it.copy(message = UiText.Resource(Res.string.documents_deleted)) } }
                .onFailure { e -> _uiState.update { it.copy(documents = before, error = e.toDocumentUiText()) } }
        }
    }

    fun open(document: StaffDocument) {
        if (_uiState.value.openingId != null) return
        _uiState.update { it.copy(openingId = document.id) }
        viewModelScope.launch {
            openDocument(document)
                .mapCatching { bytes -> opener.open(document.fileName, document.contentType, bytes) }
                .onFailure { e -> _uiState.update { it.copy(error = e.toDocumentUiText()) } }
            _uiState.update { it.copy(openingId = null) }
        }
    }

    fun messageShown() = _uiState.update { it.copy(message = null, error = null) }
}
